import { LitElement, html, unsafeCSS } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { ApiFactory } from '../../../api/agwasuite-api-factory';
import { ApiUserMaintenance } from '../../../api/core/api-user-maintenance';
import {
  LoginUser,
  LoginGroup,
  LoginSecurityContext,
  LoginUserImis,
} from '../../../api/agwa-api-model';

import { router } from '../../../router';
import { WellKnownIds } from '../../../utility/claim-id';
import { WithUserProfileConsumer } from '../../../mixins/with-user-profile-consumer';
import './user-edit-tabs.js';
import '../../app-profile/app-profile-features.js';
import {
  UserProfile,
  UserProfileData,
} from '../../../datatypes/user-profile.js';
import styles from './user-edit-styles.css?inline';
import '../../../components/agwa-page-header';
import { staffTrail } from './staff-trail';
import { personName } from '../../../utility/person-name';
import { terms } from '../../../language/terms';

@customElement('user-edit')
export class UserEdit extends WithUserProfileConsumer(LitElement) {
  @property({ type: String }) userId = '';
  @property({ type: String }) groupId = '';
  @property({ type: String }) returnUrl = '';

  @state() private user: LoginUser | null = null;
  @state() private group: LoginGroup | null = null;
  @state() private imis: LoginUserImis | null = null;
  @state() private loading = false;
  @state() private error: string | null = null;
  @state() private capabilityProfile: UserProfile | null = null;
  private userSecurityContext?: LoginSecurityContext;

  static styles = [unsafeCSS(styles)];

  /**
   * Reads the person as the page opens. The page is editable at once for everyone who opens it
   * (#398 C18, @LOCK-056): there is no edit lock on a person, and the version this read carries is
   * what a save is checked against (@LOCK-057).
   */
  connectedCallback() {
    super.connectedCallback();
    void this.fetchUserDetails();
  }

  /** Reads the person and their company. */
  async fetchUserDetails() {
    if (!this.userId || !this.groupId) return;

    this.loading = true;
    this.error = null;

    try {
      const api = ApiFactory.authenticated(ApiUserMaintenance);
      const resp = await api.getGroupUsers({
        loginGroupId: this.groupId,
        includeSecurityContexts: true, // Need security context for user editing
      });
      if (resp.success && resp.data?.users) {
        this.group = resp.data.group ?? null;

        // Find the specific user by ID
        const user = resp.data.users.find((u) => u.id === this.userId);
        if (user) {
          const securityContext = resp.data.securityContexts?.[user.id];
          this.userSecurityContext = securityContext;
          this.user = user;
          this.rebuildCapabilityProfile(user, securityContext);

          // Find iMIS data for this user
          if (resp.data.imis) {
            this.imis =
              resp.data.imis.find((i) => i.loginUserId === this.userId) ?? null;
          }
        } else {
          this.error = `${terms.loginUser.value} not found`;
          this.userSecurityContext = undefined;
          this.user = null;
          this.capabilityProfile = null;
        }
      } else {
        this.error =
          resp.error || `Failed to load ${terms.loginUser.lower} details`;
        this.userSecurityContext = undefined;
        this.user = null;
        this.capabilityProfile = null;
      }
    } catch (e) {
      this.error =
        (e as Error)?.message ||
        `Error loading ${terms.loginUser.lower} details`;
      this.userSecurityContext = undefined;
      this.user = null;
      this.capabilityProfile = null;
    } finally {
      this.loading = false;
    }
  }

  /** Cancel or Back: leaves, and nothing is written. */
  handleBackToList() {
    this.navigateBack();
  }

  private navigateBack() {
    if (this.returnUrl) {
      void router?.navigate(this.returnUrl);
    } else if (this.isInternalGroup) {
      // AGWA staff: go to AGWA staff page
      void router?.navigate('/module/staff');
    } else {
      // Member staff: go to member workspace staff tab
      void router?.navigate(`/maintenance/companies/${this.groupId}/staff`);
    }
  }

  /** Check if this is the AGWA internal group (seeded system tenant) */
  private get isInternalGroup(): boolean {
    return this.groupId.toLowerCase() === WellKnownIds.agwaGroupId;
  }

  handleEditUserSave(e: CustomEvent) {
    // The save landed: back to the staff list.
    if (e.detail) {
      this.navigateBack();
    }
  }

  /**
   * The tabs blocked or activated this person (#398 C17b). The tabs have already updated the person
   * this page passed them, in place, so their unsaved field edits stay; this page only redraws what
   * it shows from that person. It never merges the event's person over its own, which would hand the
   * tabs a new object and reset their fields. The event carries the person as a LoginUser, so it is
   * matched on `id` (the handlers this replaces read `userId`, which LoginUser does not have, and so
   * never ran).
   */
  handleUserBlockedChanged(e: CustomEvent<LoginUser>) {
    if (e.detail?.id === this.userId && this.user) {
      this.rebuildCapabilityProfile(this.user);
    }
  }

  private rebuildCapabilityProfile(
    user: LoginUser | null,
    securityContext?: LoginSecurityContext
  ) {
    if (!user) {
      this.capabilityProfile = null;
      return;
    }
    if (securityContext) {
      this.userSecurityContext = securityContext;
    }
    this.capabilityProfile = new UserProfile(this.mapUserToProfileData(user));
  }

  private mapUserToProfileData(user: LoginUser): UserProfileData {
    return {
      authProvider: 'internal',
      username: user.username,
      displayName: user.displayName,
      emailAddress: user.emailAddress,
      claims: {}, // Claims are now derived from securityContext, not stored on user
      securityContext: this.userSecurityContext,
      userId: user.id,
    };
  }

  private renderCapabilityPanel() {
    if (this.capabilityProfile) {
      return html`<app-profile-features
        .userProfile=${this.capabilityProfile}
      ></app-profile-features>`;
    }
    return html`<div class="capability-placeholder">
      <p>No capability details available.</p>
    </div>`;
  }

  render() {
    if (this.loading) {
      return html`<div class="loading">
        Loading ${terms.loginUser.lower} details...
      </div>`;
    }

    if (this.error) {
      return html`
        <div class="error">
          <p>${this.error}</p>
          <sl-button @click=${this.handleBackToList}
            >Back to ${terms.loginUser.value} List</sl-button
          >
        </div>
      `;
    }

    if (!this.user || !this.group) {
      return html`
        <div class="not-found">
          <p>${terms.loginUser.value} not found or data not available</p>
          <sl-button @click=${this.handleBackToList}
            >Back to ${terms.loginUser.value} List</sl-button
          >
        </div>
      `;
    }

    return html`
      <div class="user-edit">
        <agwa-page-header .crumbs=${staffTrail(this.group)}>
          ${
            // The page title names the person by display name as plain text, the title class of
            // D-151 contact rule 8; their username is the field being edited below.
            personName(this.user.displayName)
          }
        </agwa-page-header>

        <div class="user-edit-content">
          <div class="user-edit-form">
            <user-edit-tabs
              .group=${this.group}
              .user=${this.user}
              .imis=${this.imis}
              .currentUsername=${this.userProfile?.username ?? ''}
              @save=${this.handleEditUserSave}
              @cancel=${this.handleBackToList}
              @user-locked=${this.handleUserBlockedChanged}
              @user-activated=${this.handleUserBlockedChanged}
            ></user-edit-tabs>
          </div>
          <div class="user-edit-capabilities">
            ${this.renderCapabilityPanel()}
          </div>
        </div>
      </div>
    `;
  }
}
